<template>
  <quick-info-popup
    v-if="simulationView.simulation"
    class="simulationitem-quickinfopopup"
    :position="{ x, y }"
    :info-icon-visible="true"
    :active="infoItem.active"
    @update:active="onActiveChange($event)"
    @update:position="onPositionChange($event)"
    @close="closePopup()"
    @pointerdown="refreshTimestamp()"
    @info-click="openInfoOverlay(item)"
  >
    <template #label>{{ formatSimulationRoundName(roundIndex) }}</template>
    <template #title>{{ item?.title }}</template>

    <template
      v-if="infoItem.itemRoundIndex !== infoItem.roundIndex"
      #before-content
    >
      <template v-if="item?.type == 'incident'">
        {{ $t("labels.activatedIn") }}
      </template>
      <template v-else>
        {{ $t("labels.playedIn") }}
      </template>
      {{ formatSimulationRoundName(infoItem.itemRoundIndex) }}
    </template>

    <template #default>
      <zebra-grid>
        <template v-for="costEntry in activeActionCosts" :key="costEntry.elementUid">
          <effect-expression
            v-slot="{ value, affectedItem }"
            :expression="costEntry.expression"
            :rounds="rounds"
            :round-index="roundIndex"
            :parse-context="{
              affectedType: costEntry.affectedType,
              affectedId: costEntry.affectedId,
              targets: simulationView.targets,
              resources: simulationView.resources,
              indices
            }"
            :sign-required="true"
            impulse-type="cost"
            :source-type="item.type"
            :source-id="item.id"
          >
            <div class="quickinfo-popup__griditem">
              <span class="quickinfo-popup__item">
                {{ elements.byUid[costEntry.elementUid]?.title || "?" }}
              </span>
            </div>
            <div class="quickinfo-popup__griditem"></div>
            <div class="quickinfo-popup__griditem" :title="costEntry.expression">
              {{ value }}
            </div>
            <div class="quickinfo-popup__griditem">
              {{ affectedItem?.unit }}
            </div>
          </effect-expression>
        </template>

        <template v-for="effect in roundEffects" :key="effect.id">
          <effect-expression
            v-slot="{ value, affectedItem }"
            :effect-id="effect.id"
            :expression="effect.impulseExpressions[roundIndex - itemRoundIndex]"
            :rounds="rounds"
            :round-index="roundIndex"
            :parse-context="{
              affectedType: effect.affectedType,
              affectedId: effect.affectedId,
              targets: simulationView.targets,
              resources: simulationView.resources,
              indices
            }"
            :sign-required="true"
          >
            <div class="quickinfo-popup__griditem">
              <store-item
                :id="effect.affectedId"
                v-slot="{ item: effectItem }"
                :type="effect.affectedType"
                class="quickinfo-popup__item"
              >
                {{ effectItem.title }}
              </store-item>
            </div>
            <div class="quickinfo-popup__griditem"></div>
            <div class="quickinfo-popup__griditem">
              <template v-if="effect.visibility == 'hint'">
                <template v-if="infoItem.type != 'pendingEffect'">
                  {{ effect.hint }}
                </template>
              </template>
              <template v-else>
                {{ value }}
              </template>
            </div>
            <div class="quickinfo-popup__griditem">
              <template v-if="effect.visibility == 'visible'">
                {{ affectedItem?.unit }}
              </template>
            </div>
          </effect-expression>
        </template>
      </zebra-grid>
    </template>
  </quick-info-popup>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useSimulationViewStore } from "@editor/views/simulation/simulationView.store";
import { formatRoundName } from "@editor/items/simulationRound/useRoundTime";
import { splitUid } from "@/helpers/utility/Id";

import QuickInfoPopup from "@core/ui/QuickInfoPopup.vue";
import ZebraGrid from "@core/ui/ZebraGrid.vue";

import StoreItem from "@editor/items/StoreItem.vue";
import EffectExpression from "@editor/items/effect/EffectExpression.vue";

import { useOverlay } from "@editor/ui/overlayDialog.store";
import { useIndexStore } from "@editor/items/index/indices.store";
import { useEffectStore } from "@editor/items/effect/effects.store";
import { useActionStore } from "@editor/items/action/actions.store";
import { useSelectionStore } from "@editor/store/editor/selection.store";
import { useIncidentStore } from "@editor/items/incident/incidents.store";
import { useElements } from "@editor/items/element/elements";

import type { SimulationItemQuickInfo } from "@editor/views/simulation/simulationView.store";
import type { OverlayDialogItemRequest } from "@editor/ui/OverlayDialogItem";
import type { EffectItem } from "../effect/EffectItem";

const props = defineProps<{
  infoItem: SimulationItemQuickInfo;
}>();

const emit = defineEmits<{
  (e: "close", value: SimulationItemQuickInfo): void;
}>();

const simulationView = useSimulationViewStore();
const effectStore = useEffectStore();

const actions = computed(() => {
  return useActionStore().items;
});
const incidents = computed(() => {
  return useIncidentStore().items;
});
const indices = computed(() => useIndexStore().items);

const elements = useElements();

const itemRoundIndex = computed(() => {
  return props.infoItem.itemRoundIndex;
});
const roundIndex = computed(() => {
  return props.infoItem.roundIndex;
});

const x = computed(() => {
  return props.infoItem.x;
});
const y = computed(() => {
  return props.infoItem.y;
});

const item = computed(() => {
  const itemType = props.infoItem.itemType;
  const itemId = props.infoItem.itemId;
  if (itemType === "action") {
    return actions.value?.[itemId];
  } else if (itemType === "incident") {
    return incidents.value?.[itemId];
  }
  throw new Error(`itemType ${itemType} can't be found`);
});

const rounds = computed(() => simulationView.rounds);

const visibleEffects = computed(() => {
  if (!item.value) return [];
  const scenario = simulationView.scenario;
  const effects = effectStore.getByActivator(item.value) || [];
  if (scenario) {
    if (scenario.effectsVisibility == "hidden") return [];
    if (scenario.effectsVisibility == "visible") return effects.map((effect): EffectItem => ({ ...effect, visibility: "visible" }));
    if (scenario.effectsVisibility == "values-hidden") return effects.map((effect): EffectItem => ({...effect, visibility: "hint" }));
  }
  if (item.value?.effectsVisibility == "hidden") return [];
  if (item.value?.effectsVisibility == "visible") return effects.map((effect): EffectItem => ({ ...effect, visibility: "visible" }));
  if (item.value?.effectsVisibility == "values-hidden") return effects.map((effect): EffectItem => ({...effect, visibility: "hint" }));
  
  return effects.filter((effect) => effect.visibility !== "hidden");
});

const roundEffects = computed(() => {
  const fx = visibleEffects.value.filter((effect) => {
    const expression =
      effect.impulseExpressions[roundIndex.value - itemRoundIndex.value];
    if (expression == null || expression === "+0" || expression == "") return false;
    return true;
  });

  if (props.infoItem.type === "pendingEffect") {
    return fx;
  }
  
  return fx.map((effect) => {
    // reduce down effects over multiple rounds to a single expression
    const expressions = effect.impulseExpressions;
    const reducedExpression = expressions.reduce((expressionA, expressionB) => {
      if (!expressionB) return expressionA;
      if (!expressionA) return expressionB;

      if (expressionB.startsWith("=")) {
        if (expressionA.startsWith("=")) return expressionB;
        return `${expressionB}${expressionA}`;
      }
      if (expressionA.startsWith("-")) {
        return `+(${expressionA})${expressionB}`;
      }
      return `${expressionA}${expressionB}`;
    }, "");
    return {
      ...effect,
      impulseExpressions: [reducedExpression]
    };
  });
});

const costs = computed(() => {
  if (itemRoundIndex.value != roundIndex.value) return [];
  if (item.value.type !== "action") return [];

  return item.value.costs;
});

const activeActionCosts = computed(() => {
  return costs.value.map((cost) => {
    const { type, id } = splitUid(cost.elementUid);
    return {
      elementUid: cost.elementUid,
      expression: cost.expression,
      affectedType: type,
      affectedId: id
    };
  });
});

const affectedItems = computed(() => {
  return roundEffects.value.map((effect) => {
    return { id: effect.affectedId, type: effect.affectedType };
  });
});

// Methods

function closePopup() {
  emit("close", props.infoItem);
  onActiveChange(false);
}
function onActiveChange(active: boolean) {
  if (active) {
    update({
      lastUpdate: Date.now(),
      active
    });
    for (const affectedItem of affectedItems.value) {
      useSelectionStore().setHighlight(affectedItem);
    }
  } else {
    update({
      active
    });
    useSelectionStore().removeHighlight(item.value);
    for (const affectedItem of affectedItems.value) {
      useSelectionStore().removeHighlight(affectedItem);
    }
  }
}
function formatSimulationRoundName(roundIndex: number) {
  const simulation = simulationView.simulation;
  if (simulation == null) return roundIndex + 1;
  return formatRoundName(
    simulation.durationType,
    simulation.roundUnit,
    simulation.roundLength,
    roundIndex,
    simulation.timeStart
  );
}
function update(data: Partial<SimulationItemQuickInfo>) {
  useSimulationViewStore().updateSimulationQuickInfo({
    ...props.infoItem,
    ...data
  });
}
function refreshTimestamp() {
  update({
    lastUpdate: Date.now()
  });
}
function openInfoOverlay(item: OverlayDialogItemRequest) {
  return useOverlay().setItem(item);
}
function onPositionChange({ x, y }: { x: number; y: number }) {
  update({ x, y });
}
</script>
